import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await forgotPassword(email);
    setLoading(false);
    if (result.success) {
      setSent(true);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up">
          {sent ? (
            <div className="text-center" data-testid="reset-sent-message">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-7 w-7 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Check your email</h1>
              <p className="text-sm text-[#4B5563] mt-2 max-w-sm mx-auto">
                If an account exists with <strong>{email}</strong>, we've sent a password reset link.
              </p>
              <Link to="/login">
                <Button variant="outline" className="mt-6 rounded-xl" data-testid="back-to-login-btn">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-[#F97D6A]" />
                </div>
                <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Forgot password?</h1>
                <p className="text-sm text-[#4B5563] mt-1">No worries, we'll send you reset instructions.</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-6" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-[#1F2937] font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="mt-1.5 rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A]"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>

              <p className="text-center text-sm text-[#4B5563] mt-6">
                <Link to="/login" className="text-[#F97D6A] font-medium hover:underline inline-flex items-center gap-1">
                  <ArrowLeft className="h-3 w-3" />
                  Back to Sign In
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
